import React from "react";
import { Link } from "react-router-dom";
import { pipe } from "fp-ts/function";
import * as O from "fp-ts/Option";
import * as RA from "fp-ts/ReadonlyArray";

import { AngleRightIcon } from "src/assets/icons/angle_right_icon";
import { CheckIcon } from "src/assets/icons/check_icon";
import { PaperPlaneIcon } from "src/assets/icons/paper_plane_icon";
import { StarIcon } from "src/assets/icons/star_icon";
import { UsersIcon } from "src/assets/icons/users_icon";
import { Report } from "src/generated/types";
import { Routes } from "src/utils/routes";
import loader from "src/assets/icons/loader.gif";

import "./step_indicator.scss";

/*
 * Types
 */

export interface StepIndicatorProps {
    readonly report: Report;
}

interface Indicator {
    icon: React.ReactNode;
    readonly label: string;
    readonly route: string;
}

/*
 * Main
 */

const indicators: ReadonlyArray<Indicator> = [
    {
        icon: <CheckIcon />,
        label: "Account",
        route: Routes.account,
    },
    {
        icon: <UsersIcon />,
        label: "Constituent Groups",
        route: Routes.constituentGroups,
    },
    {
        icon: <PaperPlaneIcon />,
        label: "Survey",
        route: Routes.promotion,
    },
    {
        icon: <StarIcon />,
        label: "Report",
        route: Routes.report.home,
    },
];

const stepsIdx: ReadonlyArray<string> = [
    "accountInformation",
    "needsConstituentGroups",
    "collectingData",
    "readyToGenerate",
    "reportGenerated",
];

export const StepIndicator = (props: StepIndicatorProps): React.ReactElement => {
    const { report } = props;

    const currentStepIndex: number = pipe(
        stepsIdx,
        RA.findIndex((s) => s === report.status),
        O.getOrElse(() => 0),
    );

    const getIndicatorStatus = (idx: number) =>
        idx < currentStepIndex ? "complete" : idx === currentStepIndex ? "current" : undefined;
    const getClassNames = (idx: number): string => {
        return `a-step a-step-${idx + 1} ${getIndicatorStatus(idx)}`;
    };

    return (
        <>
            {report.status === "reportGenerating" ? (
                <div className="generating-loader">
                    <img alt="Loader icon" src={loader} />
                    <p>Your report is being generated...This could take a while. Check back shortly for updates.</p>
                </div>
            ) : (
                <div className="step-indicator-wrap">
                    {indicators.map((i, idx) => (
                        <div className={getClassNames(idx)} key={i.label}>
                            <div className="top-label">CURRENT STEP</div>
                            <div className="step-circle">{idx < currentStepIndex ? <CheckIcon /> : i.icon}</div>
                            {getIndicatorStatus(idx) !== undefined ? (
                                <Link
                                    className="bottom-label"
                                    to={{ pathname: i.route, state: { currentReportId: report.id } }}
                                >
                                    {i.label}
                                    <AngleRightIcon />
                                </Link>
                            ) : (
                                <div className="bottom-label bottom-label--disabled">
                                    {i.label}
                                    <AngleRightIcon />
                                </div>
                            )}

                            {idx < indicators.length - 1 && (
                                <div className="separator-bar">
                                    <div className="separator-bar-inner" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};
